import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postAnnouncement } from '@/lib/api/notifications';
import { queryKeys } from '@/lib/queryKeys';

interface Props {
  batchId: string;
  onSuccess?: () => void;
}

export function AnnouncementComposer({ batchId, onSuccess }: Props) {
  const [content, setContent] = useState('');
  const qc = useQueryClient();

  const { mutate, isPending, isSuccess, reset } = useMutation({
    mutationFn: () => postAnnouncement(batchId, content.trim()),
    onSuccess: () => {
      setContent('');
      qc.invalidateQueries({ queryKey: queryKeys.trainer.batchAnnouncements(batchId) });
      onSuccess?.();
      setTimeout(reset, 3000);
    },
  });

  const canSubmit = content.trim().length > 0 && content.trim().length <= 500;

  return (
    <div>
      <textarea
        className="w-full resize-none rounded-lg border border-[#ececec] bg-white px-3 py-2 text-sm text-[#111827] placeholder:text-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#ff5a1f]/40"
        rows={3}
        maxLength={500}
        placeholder="Write an announcement for all students in this batch…"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        disabled={isPending}
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-[#6b7280]">{content.length}/500</span>
        <div className="flex items-center gap-2">
          {isSuccess && (
            <span className="text-xs text-green-500">Announcement posted ✓</span>
          )}
          <button
            type="button"
            onClick={() => mutate()}
            disabled={!canSubmit || isPending}
            className="rounded-lg bg-[#ff5a1f] px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50 hover:opacity-90 transition-opacity"
          >
            {isPending ? 'Posting…' : 'Post'}
          </button>
        </div>
      </div>
    </div>
  );
}
